import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Copy, Monitor, Smartphone, ExternalLink, MessageSquare, Paperclip, X, Send, Loader2, Eye, Check } from "lucide-react";

export function PreviewShell({ url, viewing, onBack, frameKey }) {
  const [device, setDevice] = useState("desktop");
  const shown = viewing?.url || url;
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-2xl flex flex-col" data-testid="national-preview-shell">
      <div className="bg-slate-950 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-xs font-mono text-slate-400 truncate flex items-center gap-2" data-testid="national-preview-url">
          <span className={`w-2 h-2 rounded-full ${viewing ? "bg-amber-400" : "bg-emerald-500"}`} /> {shown}
        </div>
        <button data-testid="national-preview-copy" onClick={() => { navigator.clipboard.writeText(shown); toast.success("Copied"); }} className="p-1.5 text-slate-400 hover:text-slate-200"><Copy className="w-4 h-4" /></button>
        <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800">
          <button data-testid="national-preview-desktop" onClick={() => setDevice("desktop")} className={`p-1.5 rounded ${device === "desktop" ? "bg-slate-700 text-indigo-400" : "text-slate-500"}`}><Monitor className="w-4 h-4" /></button>
          <button data-testid="national-preview-mobile" onClick={() => setDevice("mobile")} className={`p-1.5 rounded ${device === "mobile" ? "bg-slate-700 text-indigo-400" : "text-slate-500"}`}><Smartphone className="w-4 h-4" /></button>
        </div>
        <a href={shown} target="_blank" rel="noreferrer" data-testid="national-preview-open" className={ui.btnGhost}><ExternalLink className="w-4 h-4" /> Open</a>
      </div>
      {viewing && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between text-xs" data-testid="national-preview-viewing">
          <span className="text-amber-300 font-medium">Viewing: {viewing.label}</span>
          <button onClick={onBack} data-testid="national-preview-back" className="text-slate-300 hover:text-white underline">Back to current version</button>
        </div>
      )}
      <div className="bg-slate-800 p-4 flex justify-center h-[640px] overflow-auto">
        <iframe key={`${shown}-${frameKey}`} title="national preview" data-testid="national-preview-iframe" src={shown}
          className="bg-white rounded-md shadow-xl border border-slate-700"
          style={{ width: device === "mobile" ? 390 : "100%", height: device === "mobile" ? 844 : "100%", maxWidth: "100%" }} />
      </div>
    </div>
  );
}

const read = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res({ name: f.name, data_base64: String(r.result).split(",")[1] }); r.onerror = rej; r.readAsDataURL(f); });

function Change({ c, i }) {
  return (
    <div className="text-xs" data-testid={`nat-edit-change-${i}`}>
      <div className="font-mono uppercase tracking-wider text-slate-500 mb-1">{c.field}</div>
      {c.field === "logo" ? (
        <div className="flex items-center gap-3">
          {c.before ? <img src={c.before} alt="old logo" className="h-14 bg-white rounded p-1 opacity-60" /> : <span className="text-slate-500">no logo</span>}
          <span className="text-slate-500">→</span><img src={c.after} alt="new logo" className="h-14 bg-white rounded p-1" />
        </div>
      ) : (<>
        <div className="rounded bg-rose-950/20 border border-rose-500/20 text-rose-200/80 p-2 mb-1 line-through decoration-rose-500/40">{c.before || "—"}</div>
        <div className="rounded bg-emerald-950/20 border border-emerald-500/20 text-emerald-200 p-2">{c.after}</div>
      </>)}
    </div>
  );
}

export function EditPanel({ basePath, live, disabled, hint, onView, onApplied, testid = "national", placeholder = 'e.g. "Make the homepage headline punchier and add an FAQ about emergency service"' }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [approving, setApproving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`${basePath}/edits/pending`).then(({ data }) => {
      if (data) { setPending(data); setText(data.instruction || ""); }
    }).catch(() => {});
  }, [basePath, live]);

  const addFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (list.some((f) => f.size > 8 * 1024 * 1024)) return toast.error("Each file must be 8 MB or smaller");
    try { const added = await Promise.all(list.map(read)); setFiles((p) => [...p, ...added].slice(0, 6)); } catch { toast.error("Could not read that file"); }
  };
  const propose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/edit-instruction`, { instruction: text.trim(), attachments: files });
      setPending(data);
      onView({ label: "Proposed changes — not published yet", url: data.preview_url });
      toast.success("Proposed changes ready — inspect the preview below");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`${basePath}/edit-approve/${pending.edit_id}`);
      toast.success(data.redeploying ? "Applied — republishing the live site and verifying…" : "Applied — new version saved");
      setPending(null); setText(""); setFiles([]); onApplied();
    } catch (e) {
      toast.error(apiErr(e));
      if (e?.response?.status === 409 || e?.response?.status === 404) { setPending(null); onView(null); }
    } finally { setApproving(false); }
  };
  const discard = async () => {
    try { await api.post(`${basePath}/edit-reject/${pending.edit_id}`); } catch { /* ignore */ }
    setPending(null); onView(null);
  };

  return (
    <div className={`${ui.card} p-6`} data-testid={`${testid}-edit-panel`}>
      <h2 className={`${ui.sectionHeader} mb-1 flex items-center gap-2`}><MessageSquare className="w-4 h-4 text-indigo-400" /> Edit by Instruction</h2>
      <p className="text-xs text-slate-500 mb-4">{hint} Attach files for reference (or an image to use as the new logo). Preview, then approve{live ? " to republish the live page" : ""}.</p>
      <textarea data-testid="national-edit-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className={ui.textarea} rows={3} />
      <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer hover:text-slate-200 mt-2">
        <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,.xlsx,.csv,.txt,.md" onChange={addFiles} className="hidden" data-testid="national-edit-file" />
        <Paperclip className="w-3.5 h-3.5" /><span className="underline">Attach files (JPG, PNG, PDF, Word, Excel, CSV, TXT)</span>
      </label>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="national-edit-attachments">
          {files.map((f, i) => <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800 text-[11px] text-slate-200">{f.name}<button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-400"><X className="w-3 h-3" /></button></span>)}
        </div>
      )}
      <div><button data-testid="national-propose-btn" onClick={propose} disabled={busy || !text.trim() || disabled} className={`${ui.btnPrimary} mt-3`}>{busy ? <Loader2 className="w-4 h-4 spin" /> : <Send className="w-4 h-4" />} {busy ? "Writing changes…" : "Propose changes"}</button></div>
      {pending && (
        <div className="mt-5 border-t border-slate-800 pt-4" data-testid="national-edit-preview">
          <p className="text-sm text-slate-200 mb-3"><span className="text-indigo-400 font-semibold">Proposed:</span> {pending.summary}</p>
          <div className="space-y-3 max-h-80 overflow-auto pr-1">{pending.changes.map((c, i) => <Change key={i} c={c} i={i} />)}</div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button data-testid="national-view-edit-btn" onClick={() => { onView({ label: "Proposed changes — not published yet", url: pending.preview_url }); document.querySelector('[data-testid="national-preview-shell"]')?.scrollIntoView({ behavior: "smooth" }); }} className={ui.btnGhost}><Eye className="w-4 h-4" /> Preview full site</button>
            <button data-testid="national-approve-btn" onClick={approve} disabled={approving || disabled} className={ui.btnPrimary}>{approving ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} {live ? "Approve & publish" : "Approve"}</button>
            <button data-testid="national-discard-btn" onClick={discard} disabled={approving} className={ui.btnGhost}>Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}
